<template>
  <div class="keditor">
    <div class="editor-meta">
      <input
        v-model="title"
        class="kb-input"
        maxlength="300"
        placeholder="Заголовок"
      >
      <select v-if="!kindLocked" v-model="kind" class="kb-input kind-select">
        <option value="doc">Документ (большой справочник)</option>
        <option value="public">Публичная заметка (правило)</option>
        <option value="personal">Личная заметка</option>
      </select>
      <div class="tags-editor">
        <div v-if="tags.length" class="tags-editor-chips">
          <span v-for="tag in tags" :key="tag" class="tag-chip mini">
            {{ tag }}
            <button class="tag-x" @click="removeTag(tag)">×</button>
          </span>
        </div>
        <div class="tag-multiselect">
          <div v-if="tagMenuOpen" class="tag-ms-overlay" @click="tagMenuOpen = false"></div>
          <button
            type="button"
            class="tag-ms-btn"
            @click="tagMenuOpen = !tagMenuOpen"
          >Теги ▾</button>
          <div v-if="tagMenuOpen" class="tag-ms-menu">
            <input
              v-model="tagDraft"
              class="tag-ms-input"
              placeholder="Новый тег (Enter)"
              maxlength="50"
              @keydown.enter.prevent="addTag"
            >
            <label v-for="t in popularTags" :key="t.tag" class="tag-ms-option">
              <input
                type="checkbox"
                :checked="tags.includes(t.tag)"
                @change="toggleExisting(t.tag)"
              >
              <span class="tag-ms-name">{{ t.tag }}</span>
              <span v-if="t.count" class="tag-ms-count">{{ t.count }}</span>
            </label>
            <div v-if="!popularTags.length" class="tag-ms-empty">
              Пока нет тегов — создайте первый выше
            </div>
          </div>
        </div>
      </div>
      <label v-if="(kind === 'public' && canManage) || kind === 'personal'" class="switch-row pin-opt">
        <input v-model="pinned" type="checkbox">
        <span>Закрепить в промпте</span>
      </label>
      <div class="editor-actions">
        <button
          class="btn primary"
          :disabled="!valid || busy"
          @click="$emit('save', payload)"
        >
          Сохранить
        </button>
        <button class="btn" :disabled="busy" @click="$emit('cancel')">
          Отмена
        </button>
      </div>
    </div>

    <div class="editor-split">
      <div class="editor-pane">
        <div class="pane-label">Markdown</div>
        <div class="editor-edit">
          <AiMarkdownEditor v-model="text" />
        </div>
      </div>
      <div class="editor-pane">
        <div class="pane-label">Предпросмотр</div>
        <div class="editor-preview">
          <AiMarkdown :text="text" :final="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AiMarkdown from './AiMarkdown.vue';
import AiMarkdownEditor from './AiMarkdownEditor.vue';

export default {
  name: 'KnowledgeEditor',
  components: { AiMarkdown, AiMarkdownEditor },
  props: {
    canManage: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    popularTags: { type: Array, default: () => [] },
    initial: { type: Object, default: null },
    kindLocked: { type: Boolean, default: false },
  },
  data() {
    return {
      title: '',
      text: '',
      kind: 'doc',
      pinned: false,
      tags: [],
      tagDraft: '',
      tagMenuOpen: false,
    };
  },
  computed: {
    valid() {
      return !!(this.text || '').trim();
    },
    payload() {
      return {
        text: (this.text || '').trim(),
        title: (this.title || '').trim() || null,
        tags: [...this.tags],
        kind: this.kind,
        pinned: this.pinned,
      };
    },
  },
  watch: {
    initial: {
      immediate: true,
      handler(value) {
        const src = value || {};
        this.title = src.title || '';
        this.text = src.text || '';
        this.kind = src.kind || 'doc';
        this.pinned = !!src.pinned;
        this.tags = [...(src.tags || [])];
        this.tagDraft = '';
        this.tagMenuOpen = false;
      },
    },
  },
  methods: {
    addTag() {
      const tag = this.tagDraft.trim().toLowerCase();
      if (tag && !this.tags.includes(tag) && this.tags.length < 20) {
        this.tags.push(tag);
      }
      this.tagDraft = '';
    },
    addExisting(tag) {
      const norm = String(tag).toLowerCase();
      if (norm && !this.tags.includes(norm) && this.tags.length < 20) {
        this.tags.push(norm);
      }
    },
    toggleExisting(tag) {
      const norm = String(tag).toLowerCase();
      const idx = this.tags.indexOf(norm);
      if (idx >= 0) this.tags.splice(idx, 1);
      else if (this.tags.length < 20) this.tags.push(norm);
    },
    removeTag(tag) {
      this.tags = this.tags.filter((item) => item !== tag);
    },
  },
};
</script>

<style scoped>
.keditor {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e5e7eb;
}

.editor-meta {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.kb-input {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  background: #fff;
}

.editor-meta .kb-input {
  flex: 0 1 280px;
}

.kind-select {
  flex: 0 0 auto;
}

.tags-editor {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.tags-editor-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.tag-multiselect {
  position: relative;
}

.tag-ms-overlay {
  position: fixed;
  inset: 0;
  z-index: 4;
}

.tag-ms-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}

.tag-ms-btn:hover {
  background: #f9fafb;
}

.tag-ms-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 5;
  min-width: 220px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tag-ms-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  font-size: 13px;
  cursor: pointer;
  border-radius: 6px;
  white-space: nowrap;
}

.tag-ms-option:hover {
  background: #f3f4f6;
}

.tag-ms-option input {
  width: 14px;
  height: 14px;
  cursor: pointer;
  flex-shrink: 0;
}

.tag-ms-name {
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag-ms-count {
  margin-left: auto;
  color: #9ca3af;
  font-size: 11px;
}

.tag-ms-empty {
  padding: 8px;
  font-size: 12px;
  color: #9ca3af;
}

.tag-chip.mini {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 1;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12.5px;
  white-space: nowrap;
}

.tag-x {
  border: none;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  padding: 0;
}

.tag-x:hover {
  color: #dc2626;
}

.tag-ms-input {
  width: 100%;
  box-sizing: border-box;
  padding: 5px 8px;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.3;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  margin-bottom: 4px;
}

.tag-ms-input:focus {
  outline: none;
  border-color: #2563eb;
  background: #fff;
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
}

.switch-row input {
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.pin-opt {
  font-size: 12px;
  color: #6b7280;
}

.editor-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.btn:hover:not(:disabled) {
  background: #f9fafb;
}

.btn.primary {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.btn.primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.editor-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: stretch;
  margin-bottom: 12px;
}

.editor-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

.editor-edit {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 320px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.editor-edit :deep(.cm-editor) {
  flex: 1;
  border: none;
}

.pane-label {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 6px;
}

.editor-preview {
  flex: 1;
  min-height: 320px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  overflow-y: auto;
  background: #fff;
}

@media (max-width: 900px) {
  .editor-split {
    grid-template-columns: 1fr;
  }
}
</style>
